//* 绘图库
import Konva from "konva";
import * as d3 from "d3";
//* 组件库
import type { UiTypes, UiTooltipFunction, UiSimplebar } from "various-ui";
import { $loading } from "various-ui";
//* 公共函数
import { Nodes } from "@/assets/js";
//* 私有组件
import type ModuleInfo from "./components/info/index.vue";
//* 组件属性
import type { DrawTips, DrawSequence, DrawMutations, DrawInitData, DrawInitOptions, DrawCoordinate } from "./src/types";
//* 样式
import css from "./index.module.less";

export default class Example {
    static view = ref<DrawTips>(); //* 当前位点的数据
    static view_area = ref<DrawTips>(); //* 当前的序列的区域数据
    static show?: () => void; //* Tips窗口激活响应事件
    static hidden?: () => void; //* Tips窗口销毁响应事件
    static info?: (visible: boolean) => void; //* 信息弹窗响应事件

    //* 数据
    rows = 0; //* 绘图列数
    cols = 0; //* 绘图行数
    size = 0; //* 切片高度
    mode = ""; //* 绘图模式
    data: DrawSequence[] = []; //* 绘图数据
    exons: UiTypes.candidate[] = []; //* 外显子区域数据
    slice = { cols: 0, index: -1, number: 0 }; //* 切片数据
    offset = { site: 0, colspan: 0, sequence: 0 }; //* 偏移位置
    sequence = ""; //* 序列字符串
    mutations: DrawMutations = {}; //* 突变列表
    coordinate: DrawCoordinate[] = []; //* 转录本区域
    cds = "";

    //* 检索状态
    area = ""; //* 检索类型区域
    radius = [] as number[][]; //* 检索坐标区域
    selects = [] as string[]; //* 检索突变类型
    message = ""; //* 加载文案

    //* 节点
    info: InstanceType<typeof ModuleInfo>; //* 信息弹窗
    tips: InstanceType<typeof UiTooltipFunction>; //* 提示窗口
    simplebar: InstanceType<typeof UiSimplebar>; //* 滚动容器
    container: HTMLDivElement; //* 绘图容器

    //* 初始化
    constructor(mode: string, data: DrawInitData, options: DrawInitOptions) {
        //* 回到页面顶部
        scrollTo({ top: 0 });

        //* 缓存数据
        this.mode = mode;
        this.info = options.info;
        this.tips = options.tips;
        this.radius = data.radius || [];
        this.selects = data.selects || [];
        this.simplebar = options.simplebar;
        this.container = options.container;
        this.mutations = data.mutations || {};
        this.coordinate = data.origin.coordinate || [];

        //* 读取文件内容转成Blob传给Worker（cdn跨域兼容）
        api.frontend(new URL("./src/worker.js", import.meta.url).href, {
            finally: () => $loading.close(),
            success: (module) => {
                //* 创建异步任务
                const worker = new Worker(URL.createObjectURL(new Blob([module], { type: "application/javascript" })));

                //* 注册worker事件响应处理函数
                worker.addEventListener("message", (response) => {
                    //* 数据解构
                    const { mode, data } = response.data;
                    switch (mode) {
                        case "init": {
                            //* 初始化数据
                            const context = "0".repeat(data?.number?.toString()?.length);
                            const width = this.container.clientWidth;

                            //* 数据缓存
                            this.rows = data?.rows || 0;
                            this.cols = data?.cols || 0;
                            this.exons = data?.exons || [];
                            this.sequence = data?.sequence || "";
                            this.cds = data?.cds || "";
                            this.offset.site = Math.ceil(new Konva.Text({ text: context, fontSize: 15, fontFamily: "sans-serif" }).getTextWidth());
                            this.offset.colspan = this.mode == "codon" ? 52 : 40;
                            this.offset.sequence = this.offset.site + (width - this.offset.site - 1223) / 2;

                            //* 数据预取
                            return worker.postMessage({ mode: "data", data: { index: 0, message: this.message } });
                        }

                        case "data": {
                            if (!data?.data?.length) {
                                //* 释放worker
                                worker.terminate();
                                //* 根据分辨率计算切片数据
                                this.slice.cols = Math.ceil((window.screen.height * 0.8) / this.offset.colspan);
                                this.slice.number = Math.ceil(this.cols / this.slice.cols);
                                //* 计算每个切片的高度
                                this.size = this.slice.cols * this.offset.colspan;
                                //* 销毁残留的节点对象
                                this.container.innerHTML = "";
                                this.container.style.height = `${this.cols * this.offset.colspan}px`;
                                //* 更新加载器
                                $loading.open({ message: this.message });
                                //* 渲染前的回调函数
                                options.setup?.(this);
                                //* 初始化模型
                                this.init();
                            } else {
                                //* 数据添加
                                this.data.push(...data.data);
                                //* 预取下一份数据
                                worker?.postMessage({ mode: "data", data: { index: data.index + 1 } });
                                //* 更新加载器
                                $loading.open({ message: data.message });
                            }

                            break;
                        }
                    }
                });

                //* 触发worker初始化函数
                worker.postMessage({
                    mode: "init",
                    data: {
                        mode: mode,
                        message: data.message,
                        sequence: data.origin.sequence,
                        coordinate: data.origin.coordinate,
                        strand: data.origin.strand,
                        chr_start_pos: data.origin.chr_start_pos,
                    },
                });
            },
        });
    }

    //* 渲染前初始化
    init() {
        //* 关闭加载器
        $loading.close();
        //* 检测是否需要初始化页面位置
        if (this.radius.length) return this.move(this.radius[0][0], true);
        else {
            return this.render();
        }
    }

    //* 页面移动函数
    move(site: number, tips?: boolean) {
        const index = this.data.findIndex((value) => value.site == site);
        if (index != -1) {
            //* 数据初始化
            const offset = Math.floor(index / this.rows) * this.offset.colspan;
            const scrollTop = this.simplebar.$el.querySelector(".ui-simplebar-container")?.scrollTop || 0;
            //* 移动到指定位置
            if (offset == scrollTop) this.render();
            else {
                //* 滚动内容区域到指定位置
                this.simplebar.$el.querySelector(".ui-simplebar-container")?.scrollTo({ top: offset });
                //* 更新视图
                this.render();
                //* 隐藏Tips窗口
                this.tips?.hidden();
            }

            //* 定位之后显示Tips窗口
            return requestAnimationFrame(() => {
                const container = this.container.querySelector(`.${css.sequences} g[index="${index}"]`);
                if (tips && container) {
                    //* 数据缓存
                    Example.view.value = { site: index, type: "", data: this.data[index], mutations: this.mutations[this.data[index].site] };
                    //* 窗口激活
                    this.info?.open(Example.view.value, container);
                }
            });
        } else {
            return this.render();
        }
    }

    //* 状态更新函数
    update(options: { radius?: number[][]; selects?: string[]; tips?: boolean; area?: string; site?: number; info?: boolean }) {
        //* 数据初始化
        this.area = options.area || this.area;
        this.radius = options.radius || this.radius;
        this.selects = options.selects || this.selects;
        //* 检测是否收起info
        if (!options.info) {
            this.info?.close();
            this.container.querySelectorAll(`.${css.sequences} .${css.site}`).forEach((node) => {
                node.setAttribute("aria-show", "");
            });
        }
        //* 检测是否收起tips
        if (!options.tips) this.tips?.hidden();
        //* 重绘图形
        if (options.radius?.length) return this.move(this.radius[0][0], options.tips);
        else if (options.site) return this.move(options.site, options.tips);
        else {
            return this.render();
        }
    }

    //* 渲染函数
    render() {
        //* 数据初始化
        const offset = this.simplebar.$el.querySelector(".ui-simplebar-container")?.scrollTop || 0; //* 距离页面顶部的距离
        //* 初始化渲染的突变类型
        const mutations = this.get_render_mutations();

        //* 绘制的范围
        const index = Math.floor(offset / this.size);
        const colspan = this.get_render_radius(index);
        colspan.forEach((data) => {
            const node = d3.select(`g.index${data.index}`);
            if (data.type == "remove") node.remove();
            else if (data.type == "render") this.create_slice(data.index, mutations);
            else {
                //* 序列状态更新
                this.container.querySelectorAll(`.${css.sequences} .${css.site}`).forEach((node) => {
                    const index = Number(node.getAttribute("index"));
                    if (index >= 0 && this.data[index]) {
                        //* 序列样式修改
                        node.setAttribute("class", this.get_sequence_class(this.data[index], mutations));
                    }
                });

                //* Tips状态更新
                this.container.querySelectorAll(`.${css.indicias} .${css.tips}`).forEach((node) => {
                    const index = Number(node.getAttribute("index"));
                    if (index && this.data[index]) {
                        node.setAttribute(
                            "class",
                            [css.tips, this.data[index].type != "intron" && css.exon, this.data[index].area == this.area && css.selector]
                                .filter((v) => v)
                                .join(" ")
                        );
                    }
                });

                //* 密码子状态更新
                this.container.querySelectorAll(`.${css.sequences} .${css.codon}`).forEach((node) => {
                    if (node.getAttribute("area") == this.area) node.setAttribute("class", `${css.codon} ${css.selector}`);
                    else {
                        node.setAttribute("class", css.codon);
                    }
                });
            }
        });
    }

    //* 创建切片
    create_slice(index: number, mutations: string[]) {
        //* 初始化图像
        const container = d3.select(this.container);
        const canvas = container.append("g").attr("class", `index${index}`);
        const indicias = canvas.append("g").attr("class", css.indicias); //* 标注区域
        const sequences = canvas.append("g").attr("class", css.sequences); //* 序列区域

        //* 数据检索
        const site = index * this.rows * this.slice.cols;
        const data = this.data.slice(site, (index + 1) * this.rows * this.slice.cols);
        const offset = { x: 0.5, y: index * this.size + 0.5 };
        data.forEach((value, index) => {
            //* 标注区域
            if (index % this.rows == 0) this.create_site(indicias, value, index);
            if (index % this.rows == this.rows - 1) {
                this.create_tips(indicias, value, site, index);
            }

            //* 序列节点
            const node = sequences
                .append("g")
                .attr("area", value.area)
                .attr("index", site + index)
                .attr("class", this.get_sequence_class(value, mutations));
            //* 检测渲染内容
            if (this.mode != "codon") this.create_char(node, value, index, offset);
            else {
                this.create_protein(node, value, index, offset);
            }

            //* 事件注册（Hover）
            this.register_hover(node, value, { site: site + index, type: "", data: value, mutations: this.mutations[value.site] });
        });

        return canvas.attr("transform", `translate(${offset.x}, ${offset.y})`);
    }

    //* 创建坐标
    create_site(node: d3.Selection<SVGGElement, unknown, null, undefined>, data: DrawSequence, index: number) {
        node.append("text")
            .attr("class", css.site)
            .attr("x", this.offset.site)
            .attr("y", Math.floor(index / this.rows) * this.offset.colspan + 14.5)
            .html(data.site.toString());
    }

    //* 创建标签
    create_tips(node: d3.Selection<SVGGElement, unknown, null, undefined>, data: DrawSequence, site: number, index: number) {
        //* 初始化数据
        const classNames = [css.tips, data.type != "intron" && css.exon, data.area == this.area && css.selector];
        //* 创建Tips节点
        const tips = node
            .append("g")
            .attr("area", data.area)
            .attr("index", site + index)
            .attr("class", classNames.filter((v) => v).join(" "))
            .attr("transform", `translate(${this.container.clientWidth - 72}, ${Math.floor(index / this.rows) * this.offset.colspan})`);

        tips.append("rect").attr("rx", 2).attr("ry", 2).attr("width", 70).attr("height", 26).attr("class", css.background);
        tips.append("text").attr("x", 36).attr("y", 14.5).attr("class", css.context).html(data.area);

        //* 事件注册（Mouse Area）
        this.register_area_mouse(tips, data);
        //* 事件注册（Hover Area）
        this.register_area_hover(tips, data, {
            site: site + index,
            type: "area",
            name: data.area,
            data: this.coordinate.find((value) => {
                return (value.type == data.type || (value.type == "exon" && data.type == "utr")) && data.number == value.number;
            }),
        });
    }

    //* 创建序列
    create_char(node: d3.Selection<SVGGElement, unknown, null, undefined>, data: DrawSequence, index: number, offsets: { x: number; y: number }) {
        //* 坐标
        const offsetX = this.offset.sequence + (index % this.rows) * 23;
        const offsetY = Math.floor(index / this.rows) * this.offset.colspan;
        //* 结构生成
        node.append("rect").attr("class", css.background).attr("width", 23).attr("height", 23);
        node.append("text").attr("class", css.context).attr("x", 11.5).attr("y", 14).html(data.char);
        node.append("rect").attr("class", css.area).attr("y", 22.5).attr("x", -0.5).attr("width", 24).attr("height", 5);
        //* 检测是否需要显示突变标识
        if (this.mutations[data.site] && this.mutations[data.site].count > 1) {
            node.append("text")
                .attr("class", `${css.context} ${css.mutation}`)
                .attr("x", 16)
                .attr("y", 2)
                .html(this.mutations[data.site].count > 9 ? "+" : this.mutations[data.site].count.toString());
        }

        return node
            .attr("x", offsetX + offsets.x)
            .attr("y", offsetY + offsets.y)
            .attr("size", 23)
            .attr("transform", `translate(${offsetX}, ${offsetY})`);
    }

    //* 创建序列（蛋白）
    create_protein(node: d3.Selection<SVGGElement, unknown, null, undefined>, data: DrawSequence, index: number, offsets: { x: number; y: number }) {
        //* 坐标
        const offsetX = this.offset.sequence + (index % this.rows) * 39;
        const offsetY = Math.floor(index / this.rows) * this.offset.colspan;
        //* 结构生成
        node.append("rect").attr("class", css.background).attr("width", 39).attr("height", 23);
        node.append("text").attr("class", css.context).attr("x", 19.5).attr("y", 14).html(data.char);
        //* 密码子结构生成
        data.codons?.forEach((value: DrawSequence, index: number) => {
            //* 初始化数据
            const codon = node
                .append("g")
                .attr("area", value.area)
                .attr("class", css.codon)
                .attr("transform", `translate(${index * 13},24)`);

            codon.append("rect").attr("class", css.background).attr("width", 13).attr("height", 12);
            codon.append("text").attr("class", css.context).attr("x", 6.5).attr("y", 8).html(value.char);
        });

        //* 检测是否需要显示突变标识
        if (this.mutations[data.site] && this.mutations[data.site].count > 1) {
            node.append("text")
                .attr("class", `${css.context} ${css.mutation}`)
                .attr("x", 26)
                .attr("y", 2)
                .html(this.mutations[data.site].count > 9 ? "+" : this.mutations[data.site].count.toString());
        }

        return node
            .attr("x", offsetX + offsets.x)
            .attr("y", offsetY + offsets.y)
            .attr("size", 39)
            .attr("transform", `translate(${offsetX}, ${offsetY})`);
    }

    //* 事件注册（Hover）
    register_hover(node: d3.Selection<SVGGElement, unknown, null, undefined>, data: DrawSequence, tip: DrawTips) {
        node.on("click", (ev) => {
            //* 数据初始化
            const container = this.container.querySelector(`.${css.sequences} g[index="${tip.site}"]`);
            if (container) {
                //* 数据缓存
                Example.view.value = tip;
                //* 取消之前的样式
                Example.info?.(false);
                //* 数据缓存
                Example.info = () => node.attr("aria-show", "");
                //* 状态更新
                node.attr("aria-show", true);
                //* 窗口激活
                this.info?.open(tip, container);
            }
        });
    }

    //* 事件注册（Hover Area）
    register_area_hover(node: d3.Selection<SVGGElement, unknown, null, undefined>, data: DrawSequence, tip: DrawTips) {
        node.on("mouseenter", (ev) => {
            //* 取消之前的样式
            Example.hidden?.();
            //* 数据缓存
            Example.view_area.value = tip;
            //* 显示事件注册
            Example.show = () => {
                //* Hover状态更新（序列）
                node.attr("aria-show", true);
                //* 选中状态更新（序列）
                this.container
                    .querySelectorAll(`.${css.sequences} g[area="${data.area}"]`)
                    .forEach((n) => !n.classList.contains(css.selector) && n.classList.add(css.selector));
            };

            //* 隐藏事件注册
            Example.hidden = () => {
                //* Hover状态更新（序列）
                node.attr("aria-show", "");
                //* 窗口关闭
                this.tips?.hidden();
                if (this.area != data.area) {
                    //* 选中状态更新（序列）
                    this.container.querySelectorAll(`.${css.sequences} g[area="${data.area}"]`).forEach((n) => n.classList.remove(css.selector));
                }
            };

            //* 手动触发
            Example.show();
            //* 显示Tips
            this.tips?.show("center", this.get_area_offset(data.site));
            //* 注册鼠标移除事件
            node.on("mouseleave", () => {
                //* 样式调整
                Example.hidden?.();
                //* 移除提示内容
                this.tips?.hidden();
                //* 移除鼠标事件
                node.on("mousemove", null);
                node.on("mouseleave", null);
            });
        });
    }

    //* 事件注册（Mouse Area）
    register_area_mouse(node: d3.Selection<SVGGElement, unknown, null, undefined>, data: DrawSequence) {
        node.on("click", () => {
            //* 数据更新
            if (this.area == data.area) {
                this.area = "";
                this.container.querySelectorAll(`.${css.indicias} g[area="${data.area}"]`).forEach((n) => n.classList.remove(css.selector));
            } else {
                //* 状态更新（重置）
                this.container.querySelectorAll(`.${css.indicias} g[area="${this.area}"]`).forEach((n) => n.classList.remove(css.selector));
                this.container.querySelectorAll(`.${css.sequences} g[area="${this.area}"]`).forEach((n) => n.classList.remove(css.selector));
                //* 状态更新
                this.container
                    .querySelectorAll(`.${css.sequences} g[area="${data.area}"]`)
                    .forEach((n) => !n.classList.contains(css.selector) && n.classList.add(css.selector));
                this.container.querySelectorAll(`.${css.indicias} g[area="${data.area}"]`).forEach((n) => {
                    if (!n.classList.contains(css.selector)) {
                        n.classList.add(css.selector);
                    }
                });

                //* 数据更新
                this.area = data.area;
            }
        });
    }

    //* 类型处理（序列）
    get_sequence_class(data: DrawSequence, mutations: string[]) {
        //* 初始化数据
        const result = [css.site, css[data.type]];
        //* 返回第一个存在数据的突变类型颜色
        for (const i in mutations) {
            if (this.mutations[data.site]?.data?.[mutations[i]]?.length > 0) {
                result.push(css[mutations[i].split(" ").join("").toLocaleLowerCase()]);
                break;
            }
        }

        //* 检索类型区域
        if (data.area == this.area) result.push(css.selector);
        if (this.radius.find((value) => data.site <= value[1] && data.site >= value[0])) {
            result.push(css.active);
        }

        return result.join(" ");
    }

    //* 范围处理
    get_render_radius(index: number) {
        //* 数据初始化
        const result = [];
        //* 待渲染的范围
        const render = { start: Math.max(index - 1, 0), end: Math.min(index + 1, this.slice.number - 1) };

        //* 循环销毁范围，判断哪些切片需销毁，哪些切片需取消渲染
        if (this.slice.index >= 0) {
            //* 待销毁的范围
            const remove = { start: Math.max(this.slice.index - 1, 0), end: Math.min(this.slice.index + 1, this.slice.number - 1) };
            for (let i = remove.start; i <= remove.end; i++) {
                if (i <= render.end && i >= render.start) result.push({ index: i, type: "update" });
                else {
                    result.push({ index: i, type: "remove" });
                }
            }
        }

        //* 循环渲染范围
        this.slice.index = index;
        for (let i = render.start; i <= render.end; i++) {
            if (!result.find((value) => value.index == i)) {
                result.push({ index: i, type: "render" });
            }
        }

        return result;
    }

    //* 突变类型
    get_render_mutations() {
        //* 初始化数据
        const config = {
            keys: ["Pathogenic", "Likely Pathogenic", "Likely Benign", "Benign", "Uncertain"],
            result: <string[]>[],
        };

        //* 获取当前检索的突变类型
        config.keys.forEach((select) => this.selects.includes(select) && config.result.push(select));

        return config.result;
    }

    //* 索引位置
    get_tips_offset(index: number) {
        //* 数据初始化
        const result = { offsetX: 0, offsetY: Nodes.offsetTop(Math.floor(index / this.rows) * this.offset.colspan, this.tips.$el) };
        if (this.mode == "codon") {
            result.offsetX = Nodes.offsetLeft(this.offset.sequence + (index % this.rows) * 39 + 20, this.tips.$el);
            result.offsetY = result.offsetY + 18;
        } else {
            result.offsetX = Nodes.offsetLeft(this.offset.sequence + (index % this.rows) * 23 + 12, this.tips.$el);
            result.offsetY = result.offsetY + 14;
        }

        return result;
    }

    //* 区域位置
    get_area_offset(index: number) {
        //* 数据初始化
        const scrollTop = this.simplebar.$el.querySelector(".ui-simplebar-container")?.scrollTop || 0;
        return {
            pageX: Nodes.offsetLeft(this.container.clientWidth - 72, this.tips.$el) + 35,
            pageY: Nodes.offsetTop(Math.max(Math.floor(index / this.rows) * this.offset.colspan - scrollTop, 0), this.tips.$el) + 13,
            initialization: true,
        };
    }
}
